import { Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { AdvisoryService } from "../../../Component/Api/Advisory/AdvisoryService";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { EntriesPerPage } from "../../../Component/Model/Table/Table";
import { ClientRegistration, ClientRegistrationSearchRequest } from "../../../Component/Model/Dto/Advisory/ClientRegistration";
import { ClientRegistrationsDataTableColumns } from "./ClientRegistrationsDataTableColumns";
import { useResetRecoilState, useSetRecoilState } from "recoil";
import { ClientRegistrationAtom } from "../../../State/Advisory/ClientRegistration.Atom";
import { BBBEEScorecardAtom } from "../../../State/BBBEEScorecard.Atom";
import { ConsolidationAtom } from "../../../State/Consolidation.Atom";
import { EEScorecardAtom } from "../../../State/EEScorecard.Atom";
import { OrganisationAtom } from "../../../State/Organisation.Atom";
import { ScorecardAtom } from "../../../State/Scorecard.Atom";
import { WSPScorecardAtom } from "../../../State/WSPScorecard.Atom";

export const ClientRegistrations = () => {
    const [clientRegistrationData, setClientRegistrationData] = useState<ClientRegistration[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
    const [totalItemCount, setTotalItemCount] = useState<number>(0);

    const resetScorecard = useResetRecoilState(ScorecardAtom);
    const resetScorecardData = useResetRecoilState(EEScorecardAtom);
    const resetScorecardType = useResetRecoilState(ConsolidationAtom);
    const resetWSPScorecard = useResetRecoilState(WSPScorecardAtom);
    const resetClientRegistrations = useResetRecoilState(ClientRegistrationAtom);
    const resetOrganisation = useResetRecoilState(OrganisationAtom)
    const resetBBBEEScorecard = useResetRecoilState(BBBEEScorecardAtom);
    const setClientRegistration = useSetRecoilState(ClientRegistrationAtom);

    useEffect(() => {
        resetScorecard();
        resetScorecardData();
        resetScorecardType();
        resetWSPScorecard();
        resetClientRegistrations();
        resetOrganisation();
        resetBBBEEScorecard();
    }, []);

    const clientRegistrationSearchRequest: ClientRegistrationSearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        columnSortOptions: columnSortOptions
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    useEffect(() => {
        fetchClientRegistrations();
    }, [entriesPerPage,
        pageNumber,
        columnSortOptions,
        searchValue
    ]);

    const fetchClientRegistrations = async () => {
        setIsLoading(true);
        try {
            const clientRegistrationResult = await AdvisoryService.searchClientRegistrations(clientRegistrationSearchRequest);
            setClientRegistrationData(clientRegistrationResult.data.results);
            setFilteredItemCount(clientRegistrationResult.data.filteredItemsCount);
            setTotalItemCount(clientRegistrationResult.data.totalItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const clientRegistrationTable = ClientRegistrationsDataTableColumns(clientRegistrationData, setClientRegistration);
    return (
        <>
            <Typography variant="h4" style={{ paddingBottom: "20px" }}>
                Client Registrations
            </Typography>

            <DataTable
                table={clientRegistrationTable}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemCount}
                totalItemsCount={totalItemCount}
                isLoading={isLoading}
                manualSorting={true}
                canSearch
                searchValueChanged={searchValueChanged}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                columnSortingChanged={columnSortingChanged} />
        </>
    )
}

